import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, PlayCircle } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { PhoneClip } from "@/components/landing/PhoneClip";
import { EXAMPLES } from "@/components/landing/data";
import { StatBubbles, StatChips } from "@/components/landing/StatBubbles";

const Stat = ({ n, label }) => (
  <div><div className="font-display text-3xl font-bold text-white">{n}</div><div className="text-xs text-slate-500 mt-1">{label}</div></div>
);

export const Hero = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  return (
    <section className="relative pt-32 sm:pt-40 pb-6 sm:pb-20 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 cx-grid-bg opacity-40 pointer-events-none" />
      <div className="absolute -top-40 -left-40 w-[620px] h-[620px] rounded-full bg-[#2f7bff]/20 blur-[140px] pointer-events-none" />
      <div className="absolute top-20 right-[-200px] w-[560px] h-[560px] rounded-full bg-[#a746ff]/20 blur-[140px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.15fr_0.85fr] gap-4 lg:gap-14 items-center">
        <div className="cx-rise">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#3ad7ff]/30 bg-[#3ad7ff]/[0.06] px-3 py-1.5 text-xs text-[#3ad7ff] font-mono" data-testid="hero-badge">
            <Sparkles className="w-3.5 h-3.5" />{t("ld.badge")}
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white mt-6 leading-[1.05]" data-testid="hero-title">
            {t("ld.h1a")}<br /><span className="cx-text-grad">{t("ld.h1b")}</span>
          </h1>
          <p className="text-slate-400 text-base sm:text-lg mt-6 max-w-xl leading-relaxed">{t("ld.sub")}</p>
          <div className="flex flex-wrap items-center gap-3 mt-9">
            {user ? (
              <Link to="/dashboard" data-testid="hero-cta-open-app" className="group cx-btn rounded-full px-7 py-3.5 font-semibold flex items-center gap-2">
                {t("ld.open_app")}<ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            ) : (<>
              <Link to="/login?mode=register" data-testid="hero-cta-register" className="group cx-btn rounded-full px-7 py-3.5 font-semibold flex items-center gap-2">
                {t("ld.cta")}<ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/login?mode=register" data-testid="hero-cta-try" className="cx-try-btn rounded-full px-6 py-3.5 text-white font-semibold flex items-center gap-2"><PlayCircle className="w-4 h-4 text-[#ff9df0]" />{t("ld.try_free")}</Link>
            </>)}
          </div>
          <p className="text-xs text-slate-500 mt-4 font-mono" data-testid="hero-free-hint">{t("ld.cta_hint")}</p>
          <div className="flex gap-10 mt-12 pt-8 border-t border-white/[0.07] max-w-md">
            <Stat n="30" label={t("ld.stat1")} /><Stat n="50" label={t("ld.stat2")} /><Stat n="50" label={t("ld.stat4")} /><Stat n="3" label={t("ld.stat3")} />
          </div>
        </div>
        <div className="relative mx-auto w-[260px] sm:w-[300px] lg:min-h-[600px] flex flex-col justify-center cx-clip-hover" data-testid="hero-phone-preview">
          <StatBubbles />
          <div className="h-[525px] sm:h-[595px] lg:h-auto flex flex-col justify-end lg:justify-center"><PhoneClip ex={EXAMPLES[0]} title={t("ld.ex_0")} testid="hero-phone" cycle={3200} /></div>
          <p className="text-center text-[11px] text-slate-500 mt-2" data-testid="hero-format-hint">{t("ld.formats_hint")}</p>
          <StatChips />
        </div>
      </div>
    </section>
  );
};
